import React, { FC, useContext, useEffect, useRef } from 'react';
import { Box } from '@mui/system';
import { Divider, Typography } from '@mui/material';
import CustomAvatar from '../CustomAvatar/CustomAvatar';
import { COLORS } from '@cellebrite/saas-ui-common';
import { dateFormatter } from '../../utils/formatters';
import CommentReactions from '../CommentReactions/CommentReactions';
import { ImagesCarousel } from '../index';
import MultiMarkedText from '../MultiMarkedText/MultiMarkedText';
import { GlobalContext } from '../../contexts/globalContext';
import { getKeywordsFromWatchLists } from '../../utils/utils';

interface CommentsTreeProps {
    commentHead: any;
    platform: string;
    nodeDepth?: number;
    isLastChild?: boolean;
    isLastParent?: boolean;
    markedUser?: string;
}

const CommentsTree: FC<CommentsTreeProps> = ({
    commentHead,
    platform,
    nodeDepth,
    isLastChild,
    isLastParent,
    markedUser,
}) => {
    const { reportData } = useContext(GlobalContext);
    const finalNodeDepth = nodeDepth || 0;
    const isChild = finalNodeDepth;
    const isCurrentMarked = commentHead.author?.name === markedUser;
    const cardRef = useRef(null);

    useEffect(() => {
        if (isCurrentMarked) {
            cardRef.current?.scrollIntoView();
        }
    }, [isCurrentMarked]);

    const commentsKeywords = getKeywordsFromWatchLists(
        commentHead?.watchlist,
        reportData.currentHighlightsWatchlist?.id,
    );
    return (
        commentHead && (
            <Box
                sx={{
                    display: 'flex',
                    ml: `${finalNodeDepth ? `${isChild && finalNodeDepth > 1 ? '34' : '35'}` : '8'}px`,
                    pt: 1,
                    pb: 2,
                }}
            >
                {!!commentHead.comments?.length && (
                    <Divider sx={{ borderColor: COLORS.MUTED_SHADY, mt: 5 }} orientation="vertical" flexItem />
                )}
                {isLastChild && (
                    <Divider
                        sx={{ borderColor: 'white', left: '-36px', top: '20px', position: 'relative' }}
                        orientation="vertical"
                        flexItem
                    />
                )}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                        }}
                    >
                        {!!isChild && (
                            <Divider
                                sx={{ width: 16, mr: 2.5, ml: '-36px', height: 20, borderColor: COLORS.MUTED_SHADY }}
                            />
                        )}
                        <CustomAvatar
                            styles={{
                                mt: 1,
                                zIndex: 1,
                                marginLeft: '-12px',
                            }}
                            avatarSize={'24px'}
                            avatar={{
                                source: null,
                                imgSrc: commentHead.author?.profileImageUrl,
                            }}
                        />
                        <Box>
                            <Box
                                sx={{
                                    ml: 0.5,
                                    p: 1,
                                    width: 312,
                                    boxShadow: '0px 24px 48px 0px #919EAB3D,0px 0px 4px 0px #919EAB3D',
                                    background: isCurrentMarked ? 'rgba(189, 195, 200, 1)' : 'rgba(255, 255, 255, 1)',
                                    borderRadius: '8px',
                                }}
                                ref={cardRef}
                            >
                                {reportData.displayHighlights ? (
                                    <MultiMarkedText
                                        containerProps={{
                                            className: 'color-mh',
                                            variant: 's14w500',
                                            sx: {
                                                'wordBreak': 'break-word',
                                                '& mark': {
                                                    backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                                },
                                            },
                                        }}
                                        searchTextArr={commentsKeywords}
                                    >
                                        {commentHead.author.name}
                                    </MultiMarkedText>
                                ) : (
                                    <Typography variant="s14w500" className="color-mh">
                                        {commentHead.author.name}
                                    </Typography>
                                )}
                                {reportData.displayHighlights ? (
                                    <MultiMarkedText
                                        containerProps={{
                                            className: 'color-mh',
                                            sx: {
                                                'wordBreak': 'break-word',
                                                '& mark': {
                                                    backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                                },
                                            },
                                        }}
                                        searchTextArr={commentsKeywords}
                                    >
                                        {commentHead.body}
                                    </MultiMarkedText>
                                ) : (
                                    <Typography sx={{ wordBreak: 'break-word' }} className="color-mh">
                                        {commentHead.body}
                                    </Typography>
                                )}
                                <ImagesCarousel
                                    keepFailedImages={true}
                                    items={commentHead.attachments?.map((attachment: any) => {
                                        return {
                                            src: attachment.urls?.[0],
                                            imageProps: {
                                                contain: true,
                                                videoType: attachment.title === 'Video',
                                            },
                                        };
                                    })}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="s12w400" className="color-md" sx={{ mt: 1, ml: 0.5 }}>
                                    {dateFormatter(commentHead.datePublished?.datePublished)}
                                </Typography>
                                <CommentReactions comment={commentHead} platform={platform} />
                            </Box>
                        </Box>
                    </Box>
                    {commentHead.comments?.map((childComment: any, index) => (
                        <CommentsTree
                            key={index}
                            commentHead={childComment}
                            platform={platform}
                            nodeDepth={finalNodeDepth + 1}
                            isLastChild={commentHead.comments.length === index + 1}
                            isLastParent={isLastChild}
                            markedUser={isCurrentMarked ? null : markedUser}
                        />
                    ))}
                </Box>
            </Box>
        )
    );
};

export default CommentsTree;
